import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock } from '../ui';
import SmartDispensationComposer from '../components/SmartDispensationComposer';
import { useDispensationRoutes } from '../components/dispensationUi';

const copy = PHARMACY_COPY.DISPENSATIONS;
const EMPTY_FORM = { patient_name: '', notes: '', items: [] };

export default function PharmacyDispensationCreatePage({ embedded = false, onDone }) {
  const navigate = useNavigate();
  const routes = useDispensationRoutes();
  const { toast, workSiteId, workSites, bumpReload } = usePharmacy();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [interactionPreview, setInteractionPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const workSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const medRes = await pharmacyService.listMedications({ status: 'ACTIVE' });
        if (cancelled) return;
        setMedications(medRes.success ? medRes.data || [] : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ids = form.items.map((l) => l.medication_id).filter(Boolean);
    if (ids.length < 2) {
      setInteractionPreview(null);
      return undefined;
    }
    let cancelled = false;
    pharmacyService.checkInteractions(ids).then((res) => {
      if (!cancelled && res.success) setInteractionPreview(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [form.items]);

  const lineCount = useMemo(
    () => form.items.filter((l) => l.medication_id && l.quantity).length,
    [form.items]
  );
  const canSave = lineCount > 0 && workSiteId && !busy;

  const goBack = () => {
    if (embedded && onDone) onDone();
    else navigate(routes.LIST);
  };

  const submit = async () => {
    if (!workSiteId) {
      toast('warning', copy.SITE_REQUIRED);
      return;
    }
    const items = form.items
      .filter((l) => l.medication_id && l.quantity)
      .map((l) => ({
        medication_id: l.medication_id,
        quantity: l.quantity,
        dosage_instructions: l.dosage_instructions
      }))
      .reverse();
    if (!items.length) {
      toast('warning', copy.LINES_HINT);
      return;
    }
    setBusy(true);
    try {
      const res = await pharmacyService.createDispensation({
        site_id: workSiteId,
        patient_name: form.patient_name,
        notes: form.notes,
        items
      });
      if (res.success) {
        toast('success', copy.CREATE_OK.replace('{number}', res.data.dispensation_number));
        bumpReload?.();
        if (embedded && onDone) onDone(res.data);
        else navigate(routes.DETAIL(res.data.id), { replace: true });
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`cp-order-page${embedded ? ' cp-order-page--embedded' : ''}`}>
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.CREATE_TITLE}
        description={copy.CREATE_DESC}
        actions={
          <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack}>
            <Icon name="chevronLeft" size={16} aria-hidden />
            {copy.BACK_TO_LIST}
          </button>
        }
      />

      {!workSiteId ? (
        <div className="cp-empty-state cp-card">
          <p className="mb-0">{copy.SITE_REQUIRED}</p>
        </div>
      ) : loading ? (
        <LoadingBlock label="Préparation du formulaire…" />
      ) : (
        <>
          <SmartDispensationComposer
            medications={medications}
            siteId={workSiteId}
            siteLabel={workSite?.name || ''}
            value={form}
            onChange={setForm}
            copy={copy}
            interactionPreview={interactionPreview}
          />

          <div className="cp-order-page__actions">
            <div className="cp-order-page__actions-meta">
              <strong>{lineCount}</strong>
              <span>{copy.RESULTS_LINES}</span>
            </div>
            <div className="cp-order-page__actions-btns">
              <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack} disabled={busy}>
                {copy.CANCEL}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                onClick={submit}
                disabled={!canSave}
              >
                {busy ? copy.SAVING : copy.SUBMIT}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
